import { useEffect, useRef, useState, type RefObject } from 'react';

/** The footer counts down in minutes, so the shared clock ticks once a minute. */
export const POLL_CLOCK_TICK_MS = 60_000;

interface Subscriber {
  visible: boolean;
  closesAtMs: number | null;
  onTick: (now: number) => void;
  onEnd: () => void;
}

// One clock for the whole feed (TECH-197 § Time and ending): one interval however many cards are
// mounted, running only while the page is visible and at least one subscribed card is on screen.
const subscribers = new Set<Subscriber>();
let interval: ReturnType<typeof setInterval> | null = null;

const pageVisible = (): boolean =>
  typeof document === 'undefined' || document.visibilityState !== 'hidden';

const refresh = (subscriber: Subscriber, now: number): void => {
  subscriber.onTick(now);
  // Past its end time: ask the card to read the poll again. Every tick while it is still open and
  // on screen, so a re-read that lands before the server's own clock gets there is asked again;
  // `rereadPollQuestion` caps and spaces those reads.
  if (subscriber.closesAtMs !== null && now >= subscriber.closesAtMs) subscriber.onEnd();
};

const tick = (): void => {
  const now = Date.now();
  for (const subscriber of subscribers) if (subscriber.visible) refresh(subscriber, now);
};

const sync = (): void => {
  const run = pageVisible() && [...subscribers].some(s => s.visible);
  if (run && interval === null) interval = setInterval(tick, POLL_CLOCK_TICK_MS);
  if (!run && interval !== null) {
    clearInterval(interval);
    interval = null;
  }
};

const onVisibilityChange = (): void => {
  // Back on the page: bring every visible card up to date at once, then keep ticking.
  if (pageVisible()) tick();
  sync();
};

// setTimeout fires at once past 2^31 - 1 ms (about 24.8 days), and a poll can run 30 days.
const MAX_TIMEOUT_MS = 2_147_483_647;

/**
 * One timeout at the card's `closes_at`, so the end lands within moments of it rather than at the
 * next minute tick (the minute interval is not aligned to any card's end). A far end is waited for
 * in steps under the timer limit. A card off screen or on a hidden page is caught up when it shows.
 */
const scheduleEnd = (subscriber: Subscriber): (() => void) => {
  const { closesAtMs } = subscriber;
  if (closesAtMs === null) return () => undefined;
  let timeout: ReturnType<typeof setTimeout> | null = null;
  const arm = () => {
    const wait = closesAtMs - Date.now();
    if (wait <= 0) {
      timeout = null;
      if (subscriber.visible && pageVisible()) refresh(subscriber, Date.now());
      return;
    }
    timeout = setTimeout(arm, Math.min(wait, MAX_TIMEOUT_MS));
  };
  // Already past its end: the tick and the scroll-into-view paths handle it.
  if (closesAtMs > Date.now()) arm();
  return () => {
    if (timeout !== null) clearTimeout(timeout);
  };
};

const register = (subscriber: Subscriber) => {
  if (subscribers.size === 0 && typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', onVisibilityChange);
  }
  subscribers.add(subscriber);
  const cancelEnd = scheduleEnd(subscriber);
  return {
    setVisible(visible: boolean) {
      const wasVisible = subscriber.visible;
      subscriber.visible = visible;
      // Scrolled into view: its time is brought up to date now, not at the next tick.
      if (visible && !wasVisible && pageVisible()) refresh(subscriber, Date.now());
      sync();
    },
    unregister() {
      cancelEnd();
      subscribers.delete(subscriber);
      if (subscribers.size === 0 && typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisibilityChange);
      }
      sync();
    },
  };
};

export interface UsePollClockOptions {
  /** The card's element: the clock ticks for it only while it is on screen. */
  targetRef: RefObject<Element | null>;
  closesAt: string | null;
  /** False for a poll with nothing to count down (no end time, or ended). */
  enabled: boolean;
  /** Called on a tick at or after `closesAt`: the card reads the poll again. */
  onEnd: () => void;
}

/**
 * The current time for a poll card's countdown, from the one shared minute clock, paused while the
 * page is hidden (`document.visibilityState`) or the card is off screen (an IntersectionObserver
 * per subscription). Returns the time of the card's last tick.
 */
export const usePollClock = ({
  targetRef,
  closesAt,
  enabled,
  onEnd,
}: UsePollClockOptions): number => {
  const [now, setNow] = useState(() => Date.now());
  const onEndRef = useRef(onEnd);
  useEffect(() => {
    onEndRef.current = onEnd;
  });

  useEffect(() => {
    if (!enabled) return undefined;
    const closesAtMs = closesAt === null ? null : Date.parse(closesAt);
    const subscription = register({
      visible: false,
      closesAtMs: Number.isNaN(closesAtMs) ? null : closesAtMs,
      onTick: setNow,
      onEnd: () => {
        onEndRef.current();
      },
    });
    const element = targetRef.current;
    let observer: IntersectionObserver | null = null;
    if (element && typeof IntersectionObserver !== 'undefined') {
      observer = new IntersectionObserver(entries => {
        for (const entry of entries) subscription.setVisible(entry.isIntersecting);
      });
      observer.observe(element);
    } else {
      subscription.setVisible(true);
    }
    return () => {
      observer?.disconnect();
      subscription.unregister();
    };
  }, [enabled, closesAt, targetRef]);

  return now;
};
